/**
 * This file is to map if else condition , from and to BE structure, it should contain at least two functions, one that maps
 * from BE to FE structure, and the other one that maps from FE to BE structure
 */
import { ConditionEvents } from "assets/js/scenes/automations/containers/editor/EventsConfigs";
import {
    getEdge,
    getEndCoachmarkNode,
    getStatsStepName,
    getNextTarget,
    getShadowsForStep,
} from "assets/js/scenes/automations/containers/editor/utils/utils";
import {
    getCondition,
    getSingleSwitch,
    getConditionsValues,
    mapCampaignActivitiesToBECorresponders,
    constructOutputCallerVar,
    constructSwitchConditionJson,
    getEvaluationSubAutomationName,
    getNextOfBranch
} from "../IfElse/utils/generalUtils";
import {
    getContactDetailsEvaluationSubAutomationName,
    formatRegex,
    formatIntValues,
} from "../IfElse/utils/contactDetailsUtils";
import deepcopy from "deepcopy";

// need a function to extract the conditions from the switch block

const parseBESwitchCaseToFE = (automation, mainStep, subStep, targetId) => {
    const events = [];
    const switchCases = subStep.args.switch || [];
    const links = [];
        
    const conditionsDef = () => {
        if (switchCases.length === 0) return [];
        
        const seenOutputs = new Set();
        
        return switchCases
            .map((switchCase, index) => {
                // Extract the output variable (e.g., "${n10640_output}")
                const varObject = switchCase.condition.eq?.var || 
                                  switchCase.condition.ne?.var ||
                                  switchCase.condition.ge?.var ||
                                  switchCase.condition.le?.var ||
                                  switchCase.condition.gt?.var ||
                                  switchCase.condition.lt?.var;
                
                if (varObject) {
                    // Check if we've already seen this output
                    if (seenOutputs.has(varObject)) {
                        return {
                            id: `${Date.now()}${index}`,
                            op: "or",
                            selectedTags: [''],
                            type: "ContactDetails",
                            value: "hasTag"
                        }
                    }
                    seenOutputs.add(varObject);
                    return getCondition(subStep, automation, switchCase.condition, index);

                }
                return {
                    id: `${Date.now()}${index}`,
                    op: "or",
                    selectedTags: [''],
                    type: "ContactDetails",
                    value: "hasTag"
                }
            })
            .filter(Boolean);
    };
    const defs = conditionsDef();
    
    events.push({
        id: mainStep.name,
        position: {
            x: 0,
            y: 0,
        },
        data: { 
            name: ConditionEvents()[4].name,
            desc: ConditionEvents()[4].desc,
            type: "condition",
            tileTitle: "app-automation-editor_events_condition-5_tileTitle",
            id: mainStep.name,
            baseId: "switchCase",
            directValue: "all",
            initialElement: false,
            conditions: {
                operator: "and",
                conditions: defs,
            },
            shadows: getShadowsForStep(automation, mainStep.name),
            configs: {
                // Restore noOfLinks from sub-automation if available, otherwise derive from switchCases/defs
                noOfLinks: automation.sub_automations?.find(sub => sub.name === mainStep.name)?.noOfLinks 
                    || (defs.length > 0 ? defs.length : switchCases.length),
                labelDefault: "Default",
            },
        },
        type: "editorNode",
    });

    // Create links for each switch case (Case 1, Case 2, Case 3, etc.)
    switchCases.forEach((switchCase, index) => {
        const branchLabel = `Case ${index + 1}`;
        // Check if switchCase has next directly, or has emit_next_step in steps
        let nextTarget = switchCase.next;
        
        // If no direct next, try to extract from emit_next_step pattern
        if (!nextTarget && switchCase.steps && switchCase.steps.length > 0) {
            const emitStep = switchCase.steps.find(s => 
                s.action === "SubAutomation.Call" && 
                s.args?.sub_automation === "emit_next_step"
            );
            if (emitStep) {
                // Use getNextOfBranch with a mock subStep structure
                const mockSubStep = {
                    args: {
                        switch: [switchCase]
                    }
                };
                nextTarget = getNextOfBranch(mockSubStep, automation);
            }
        }
        
        if (nextTarget && nextTarget !== "end") {
            links.push(
                getEdge("branchingEdge", mainStep.name, nextTarget, {
                    branchLabel: branchLabel,
                })
            );
        } else {
            const endCoachmark = getEndCoachmarkNode(0, 0);
            if (!events.find(e => e.id === endCoachmark.id)) {
                events.push(endCoachmark);
            }
            links.push(
                getEdge("branchingEdge", mainStep.name, endCoachmark.id, {
                    branchLabel: branchLabel,
                })
            );
        }
    });

    // Add default branch (from subStep.next - the fallback)
    if (targetId && targetId != "end") {
        links.push(
            getEdge("branchingEdge", mainStep.name, targetId, { branchLabel: "Default" })
        );
    } else {
        const defaultEndCoachmark = getEndCoachmarkNode(0, 0);
        if (!events.find(e => e.id === defaultEndCoachmark.id)) {
            events.push(defaultEndCoachmark);
        }
        links.push(
            getEdge("branchingEdge", mainStep.name, defaultEndCoachmark.id, {
                branchLabel: "Default",
            })
        );
    }

    return {
        events: events,
        links: links,
    };
};
const parseFESwitchCaseToBE = (FEEvent, connectedEdges, events) => {
    let multipleLists = events.filter(event => event.data.type === "trigger" && event.data.baseId !== "manual").length > 1 && limitsFeaturesAccess(LIMIT_FEATURE.USE_EMAIL_API);
    const mainSteps = [];
    const subAutomationsNames = [];
    const switchConditions = [];
    const prepareSingleMainStep = (condition, index) => {
        let conditionValues = [];
        conditionValues = getConditionsValues(condition, FEEvent.id);
        conditionValues.forEach((conditionValue) => {
            if (conditionValue) {
                // condition value here is the emails step id
                const stepName = getStatsStepName(FEEvent.id, index);
                if (mainSteps.filter((step) => step.name == stepName).length == 0) {
                    mainSteps.push({
                        name: stepName,
                        action: "SubAutomation.Call",
                        args: {
                            sub_automation: conditionValue.startsWith("a6") ? "sub_get_email_summary_stats" : "sub_get_stats",
                            params: {
                                trigger_id: "${input.data.step_trigger." + conditionValue + "}",
                            },
                        },
                    });
                }
            }
        });
    };
    /**
     *
     * @param {*} type  : one of [clickthru, opened,sent]
     * * @param {*} triggerIds  : ids of email address that will be evaluated, should be an array
     */
    const prepareLoopMainStep = (type, triggerIds, index, subAutomationName) => {
        return {
            name: `${FEEvent.id}${index}`,
            action: "SubAutomation.Call",
            args: {
                params: {
                    trigger_ids: triggerIds,
                    type: type,
                },
                sub_automation: subAutomationName,
            },
        };
    };
    const handleCampaignActivityCondition = (actualCondition, index) => {
        const condition = deepcopy(actualCondition);
        if (condition.campaignsOption == "specific") {
            // get the switch object and push it to the corresponding array
            const currentSwitch = getSingleSwitch(
                condition,
                FEEvent.id,
                `${FEEvent.id}${index}`
            );
            switchConditions.push(currentSwitch);
            // the below function will create the main step and push it to the corresponding array
            prepareSingleMainStep(condition, index);
            if (condition.selectedCamps.length > 0 && condition.selectedCamps[0].startsWith("a6")) {
                subAutomationsNames.push("sub_get_email_summary_stats");
            }
            else {
                subAutomationsNames.push("sub_get_stats");
            }
        } else {
            switch (condition.op) {
                case "and": {
                    const subautomationName = getEvaluationSubAutomationName(
                        condition.value
                    );
                    // get the switch object and push it to the corresponding array
                    const conditionVar = constructOutputCallerVar(
                        `${FEEvent.id}${index}`
                    );
                    const currentSwitch = constructSwitchConditionJson(
                        "eq",
                        conditionVar,
                        true
                    );
                    switchConditions.push(currentSwitch);
                    // need to add the "get_last_5" sub_automation at the top of the list to make sure it's accessible to all subautomations
                    // when needed
                    if (
                        condition.campaignsOption == "last5" &&
                        mainSteps.filter((name) => name == "get_last_5").length == 0
                    ) {
                        mainSteps.unshift({
                            action: "SubAutomation.Call",
                            args: {
                                sub_automation: "get_last_5_of_list",
                            },
                            name: "get_last_5",
                        });
                        subAutomationsNames.push("get_last_5_of_list");
                    }
                    // we need to pass down the name of the sub_automation step that calculates the 5 last emails
                    mainSteps.push(
                        prepareLoopMainStep(
                            mapCampaignActivitiesToBECorresponders(condition.value),
                            //input.data.outputs is the parameter holding all the outputs
                            condition.value == "5"
                                ? "${get_last_5_output}"
                                : "${input.data.outputs}",
                            index,
                            subautomationName
                        )
                    );
                    subAutomationsNames.push(subautomationName);
                    break;
                }
                case "or": {
                    const subautomationName = getEvaluationSubAutomationName(
                        condition.value,
                        true
                    );
                    // get the switch object and push it to the corresponding array
                    const conditionVar = constructOutputCallerVar(
                        `${FEEvent.id}${index}`
                    );
                    const currentSwitch = constructSwitchConditionJson(
                        "eq",
                        conditionVar,
                        false
                    );
                    switchConditions.push(currentSwitch);
                    // need to add the "get_last_5" sub_automation at the top of the list to make sure it's accessible to all subautomations
                    // when needed
                    if (
                        condition.campaignsOption == "last5" &&
                        mainSteps.filter((name) => name == "get_last_5").length == 0
                    ) {
                        mainSteps.unshift({
                            action: "SubAutomation.Call",
                            args: {
                                sub_automation: "get_last_5_of_list",
                            },
                            name: "get_last_5",
                        });
                        subAutomationsNames.push("get_last_5_of_list");
                    }
                    // the below function will create the main step and push it to the corresponding array
                    mainSteps.push(
                        prepareLoopMainStep(
                            mapCampaignActivitiesToBECorresponders(condition.value),
                            condition.campaignsOption == "last5"
                                ? "${get_last_5_output}"
                                : "${input.data.outputs}",
                            index,
                            subautomationName
                        )
                    );
                    subAutomationsNames.push(subautomationName);

                    break;
                }

                default:
                    break;
            }
        }
    };
    const getListItems = (condition) => {
        if (condition.selectedAttributes[0].name == "email")
            return [{ name: "email", value: "${input.data.contact.email}" }];
        if (condition.selectedAttributes[0].name == "subscribed_on")
            return [
                { name: "subscribed_on", value: "${input.data.contact.subscribed_on}" },
            ];
        else return "${input.data.contact.custom_attributes}";
    };
    const handleTagCondition = (condition, index) => {
        if (condition.selectedTags.length > 0) {
            switch (condition.op) {
                case "or":
                    if (condition.value == "noTag") {
                        // get the switch object and push it to the corresponding array
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        const evaluationSubAutomationName =
                            "evaluate_not_any_values_in_list";
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: evaluationSubAutomationName,
                                params: {
                                    attrVal: "${input.data.contact.tags}",
                                    values: condition.selectedTags,
                                },
                            },
                        });
                        subAutomationsNames.push(evaluationSubAutomationName);
                    }
                    if (condition.value == "hasTag") {
                        // get the switch object and push it to the corresponding array
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        const evaluationSubAutomationName = "evaluate_any_values_in_list";
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: evaluationSubAutomationName,
                                params: {
                                    attrVal: "${input.data.contact.tags}",
                                    values: condition.selectedTags,
                                },
                            },
                        });
                        subAutomationsNames.push(evaluationSubAutomationName);
                    }
                    break;
                case "and": {
                    if (condition.value == "noTag") {
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: "evaluate_all_not_values_in_list",
                                params: {
                                    attrVal: "${input.data.contact.tags}",
                                    values: condition.selectedTags,
                                },
                            },
                        });
                        subAutomationsNames.push("evaluate_all_not_values_in_list");
                    }
                    if (condition.value == "hasTag") {
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: {},
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: "evaluate_all_values_in_list",
                                params: {
                                    attrVal: "${input.data.contact.tags}",
                                    values: condition.selectedTags,
                                },
                            },
                        });
                        subAutomationsNames.push("evaluate_all_values_in_list");
                    }
                }
            }
        }
    };

    const handleInterestCondition = (condition, index) => {
        if (condition.contactInterests && condition?.contactInterests?.values.length > 0) {
            let selectedInterests = [];
            condition.contactInterests.values.forEach((value) => {
                selectedInterests.push(value.name);
            });
            switch (condition.contactInterests.condition) {
                case "or":
                    // get the switch object and push it to the corresponding array
                    if (condition.contactInterests.op == "notinterested") {
                        // get the switch object and push it to the corresponding array
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: {},
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        const evaluationSubAutomationName =
                            "evaluate_not_any_values_in_list";
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: evaluationSubAutomationName,
                                params: {
                                    attrVal: "${input.data.contact.interests}",
                                    values: selectedInterests,
                                },
                            },
                        });
                        subAutomationsNames.push(evaluationSubAutomationName);
                    } else {
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        const evaluationSubAutomationName = "evaluate_any_values_in_list";
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: evaluationSubAutomationName,
                                params: {
                                    attrVal: "${input.data.contact.interests}",
                                    values: selectedInterests,
                                },
                            },
                        });
                        subAutomationsNames.push(evaluationSubAutomationName);
                    }
                    break;
                case "and": {
                    if (condition.contactInterests.op == "notinterested") {
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: "evaluate_all_not_values_in_list",
                                params: {
                                    attrVal: "${input.data.contact.interests}",
                                    values: selectedInterests,
                                },
                            },
                        });
                        subAutomationsNames.push("evaluate_all_not_values_in_list");
                    }
                    else {
                        const currentSwitch = {
                            eq: { var: `$\{${FEEvent.id}${index}_output}`, value: true },
                        };
                        switchConditions.push(currentSwitch);
                        if (
                            mainSteps.filter((step) => step.name == "getContact").length == 0
                        ) {
                            mainSteps.push({
                                action: "Contact.Get",
                                name: "getContact",
                                args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                            });
                            mainSteps.push({
                                action: "Variable.Set",
                                name: "set_output",
                                args: {
                                    "input.data.contact": "${getContact_output.body.data}",
                                },
                            });
                        }
                        mainSteps.push({
                            action: "SubAutomation.Call",
                            name: `${FEEvent.id}${index}`,
                            args: {
                                sub_automation: "evaluate_all_values_in_list",
                                params: {
                                    attrVal: "${input.data.contact.interests}",
                                    values: selectedInterests,
                                },
                            },
                        });
                        subAutomationsNames.push("evaluate_all_values_in_list");
                    }
                }
            }
        }
    };


    const handleTriggerDataCondition = (condition, index) => {
        if (condition.selectedAttributes.length > 0) {
            switch (condition.op) {
                case "or":
                    let currentSwitch = constructSwitchConditionJson(
                        "eq",
                        `$\{${FEEvent.id}${index}_output}`,
                        false
                    );
                    switchConditions.push(currentSwitch);
                    let evaluationSubAutomationName =
                        getContactDetailsEvaluationSubAutomationName(
                            condition.selectedAttributesValuesOp,
                            condition.selectedAttributes[0],
                            true,
                        );

                    mainSteps.push({
                        action: "SubAutomation.Call",
                        name: `${FEEvent.id}${index}`,
                        args: {
                            sub_automation: evaluationSubAutomationName,
                            params: {
                                attrVal: condition.selectedAttributes[0].value,
                                values: condition.selectedAttributesValues.map((val) =>
                                    formatRegex(
                                        condition.selectedAttributesValuesOp,
                                        val
                                    )),
                            },
                        },
                    });
                    subAutomationsNames.push(evaluationSubAutomationName);
                    break;
                case "and":
                    currentSwitch = constructSwitchConditionJson(
                        "eq",
                        `$\{${FEEvent.id}${index}_output}`,
                        true
                    );
                    switchConditions.push(currentSwitch);

                    evaluationSubAutomationName =
                        getContactDetailsEvaluationSubAutomationName(
                            condition.selectedAttributesValuesOp,
                            condition.selectedAttributes[0],
                            false,
                        );

                    mainSteps.push({
                        action: "SubAutomation.Call",
                        name: `${FEEvent.id}${index}`,
                        args: {
                            sub_automation: evaluationSubAutomationName,
                            params: {
                                attrVal: condition.selectedAttributes[0].value,
                                values: condition.selectedAttributesValues.map((val) =>
                                    formatRegex(
                                        condition.selectedAttributesValuesOp,
                                        val
                                    )),
                            },
                        },
                    });
                    subAutomationsNames.push(evaluationSubAutomationName);
                    break;
            }
        }
    };

    const handleContactDetailsCondition = (condition, index) => {
        switch (condition.value) {
            case "info":
                if (condition.selectedAttributes.length > 0) {
                    switch (condition.op) {
                        case "or":
                            {
                                // get the switch object and push it to the corresponding array
                                const currentSwitch = constructSwitchConditionJson(
                                    "eq",
                                    `$\{${FEEvent.id}${index}_output}`,
                                    false
                                );
                                switchConditions.push(currentSwitch);
                                if (
                                    mainSteps.filter((step) => step.name == "getContact")
                                        .length == 0
                                ) {
                                    mainSteps.push({
                                        action: "Contact.Get",
                                        name: "getContact",
                                        args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                                    });
                                    mainSteps.push({
                                        action: "Variable.Set",
                                        name: "set_output",
                                        args: {
                                            "input.data.contact": "${getContact_output.body.data}",
                                        },
                                    });
                                }
                                mainSteps.push({
                                    action: "SubAutomation.Call",
                                    name: `${FEEvent.id}${index}loop`,
                                    args: {
                                        sub_automation: "get_value_list_key",
                                        params: {
                                            listItems: getListItems(condition),
                                            key: condition.selectedAttributes[0].name,
                                        },
                                    },
                                });

                                const evaluationSubAutomationName =
                                    getContactDetailsEvaluationSubAutomationName(
                                        condition.selectedAttributesValuesOp,
                                        condition.selectedAttributes[0],
                                        true
                                    );
                                mainSteps.push({
                                    action: "SubAutomation.Call",
                                    name: `${FEEvent.id}${index}`,
                                    args: {
                                        sub_automation: evaluationSubAutomationName,
                                        params: {
                                            attrVal:
                                                condition.selectedAttributes[0].type == "datetime"
                                                    ? condition.selectedAttributes[0].name ==
                                                        "subscribed_on"
                                                        ? `\${${FEEvent.id}${index}loop_output}`
                                                        : `\${time.parse(text.replace_all(${FEEvent.id}${index}loop_output," ","T")+"Z")}`
                                                    : condition.selectedAttributes[0].type == "integer" &&
                                                        condition.selectedAttributesValuesOp != "eq" &&
                                                        condition.selectedAttributesValuesOp != "ne"
                                                        ? `\${int(${FEEvent.id}${index}loop_output)}`
                                                        : `\${${FEEvent.id}${index}loop_output}`,
                                            values:
                                                condition.selectedAttributes[0].type == "datetime"
                                                    ? condition.selectedAttributesValues.map(
                                                        (val) => `\${time.parse("${val}")}`
                                                    )
                                                    : condition.selectedAttributes[0].type == "integer"
                                                        ? condition.selectedAttributesValues.map((val) =>
                                                            formatIntValues(
                                                                condition.selectedAttributesValuesOp,
                                                                val
                                                            )
                                                        )
                                                        : condition.selectedAttributesValues.map((val) =>
                                                            formatRegex(
                                                                condition.selectedAttributesValuesOp,
                                                                val
                                                            )
                                                        ),
                                        },
                                    },
                                });
                                subAutomationsNames.push(evaluationSubAutomationName);
                                subAutomationsNames.push("get_value_list_key");
                            }
                            break;
                        case "and": {
                            // get the switch object and push it to the corresponding array
                            const currentSwitch = constructSwitchConditionJson(
                                "eq",
                                `$\{${FEEvent.id}${index}_output}`,
                                true
                            );
                            switchConditions.push(currentSwitch);
                            if (
                                mainSteps.filter((step) => step.name == "getContact").length ==
                                0
                            ) {
                                mainSteps.push({
                                    action: "Contact.Get",
                                    name: "getContact",
                                    args: { ...(multipleLists && { "list_id": "input.data.listId" }) },
                                });
                                mainSteps.push({
                                    action: "Variable.Set",
                                    name: "set_output",
                                    args: {
                                        "input.data.contact": "${getContact_output.body.data}",
                                    },
                                });
                            }
                            mainSteps.push({
                                action: "SubAutomation.Call",
                                name: `${FEEvent.id}${index}loop`,
                                args: {
                                    sub_automation: "get_value_list_key",
                                    params: {
                                        listItems: getListItems(condition),
                                        key: condition.selectedAttributes[0].name,
                                    },
                                },
                            });

                            const evaluationSubAutomationName =
                                getContactDetailsEvaluationSubAutomationName(
                                    condition.selectedAttributesValuesOp,
                                    condition.selectedAttributes[0],
                                    false
                                );

                            mainSteps.push({
                                action: "SubAutomation.Call",
                                name: `${FEEvent.id}${index}`,
                                args: {
                                    sub_automation: evaluationSubAutomationName,
                                    params: {
                                        attrVal: `\${${FEEvent.id}loop_output}`,
                                        values: condition.selectedAttributesValues,
                                    },
                                },
                            });
                            subAutomationsNames.push(evaluationSubAutomationName);
                            subAutomationsNames.push("get_value_list_key");
                        }
                    }
                }
                break;
            case "hasTag":
            case "noTag":
                handleTagCondition(condition, index);
                break;
            case "interests":
                handleInterestCondition(condition, index);
            default:
                break;
        }
    };
    FEEvent.data.conditions.conditions.forEach((condition, index) => {
        switch (condition.type) {
            case "ContactDetails":
                handleContactDetailsCondition(condition, index);
                break;
            case "CampaignActivity":
                handleCampaignActivityCondition(condition, index);
                break;
            case "ContactInterests":
                handleContactDetailsCondition(condition, index);
                break;
            case "TriggerData":
                handleTriggerDataCondition(condition, index);
                break;
            default:
                break;
        }
    });
    let shadows = [];
    let inTos = [];
    if (FEEvent.data.shadows) shadows = [...FEEvent.data.shadows];
    inTos = events
        .filter((e) => e.data.baseId == "loop")
        .filter((e) => e.data.goTo == FEEvent.id);
    if (inTos.length > 0) inTos = inTos.map((e) => `${e.id}_loop`);
    
    // Build multiple switch cases from connected edges
    const switchBranches = [];
    
    // Get expected number of case branches from configs.noOfLinks (set from dialog)
    // This ensures we create all branches even if they don't have conditions yet
    const conditionCount = FEEvent.data.conditions?.conditions?.length || 0;
    // Use noOfLinks from configs (set from dialog) - dialog is mandatory so this should always exist
    const expectedCaseCount = FEEvent.data.configs?.noOfLinks || conditionCount;
    
    // Get all case edges (Case 1, Case 2, Case 3, etc.) - sorted by case number
    const caseEdges = connectedEdges
        .filter(edge => edge.data.branchLabel && edge.data.branchLabel.startsWith("Case "))
        .sort((a, b) => {
            const numA = parseInt(a.data.branchLabel.match(/\d+/)?.[0] || "0");
            const numB = parseInt(b.data.branchLabel.match(/\d+/)?.[0] || "0");
            return numA - numB;
        });
    
    // Use the operator from FEEvent.data.conditions, default to "and"
    const operator = FEEvent.data.conditions?.operator || "and";
    
    // Always create branches for all expected cases (even if not all edges are connected)
    for (let i = 0; i < expectedCaseCount; i++) {
        const caseNumber = i + 1;
        const branchLabel = `Case ${caseNumber}`;
        
        // Find the edge for this case
        let edge = caseEdges.find(e => e.data.branchLabel === branchLabel);
        
        // If edge not found by label, try to find it by position/index
        // This handles cases where the edge label might be missing or incorrect
        if (!edge && i < caseEdges.length) {
            // Use the edge at this index as fallback
            edge = caseEdges[i];
        }
        
        // Get target - if edge exists, get the target
        // This ensures adjustNextAndJumpsStepNumbers can process it and create emit_next_step steps
        let targetId = undefined;
        if (edge) {
            // Check if target is a coachmark
            if (edge.target && edge.target.includes("coachmark_")) {
                // For coachmarks, we still want to create the branch in JSON
                // Set next to "end" - adjustNextAndJumpsStepNumbers will skip creating emit step
                targetId = "end";
            } else {
                // Get the actual target node ID
                targetId = getNextTarget([edge], events);
                // If getNextTarget returns undefined, null, empty string, or "end", try using edge.target directly
                // This handles cases where getNextTarget can't resolve the target but the edge.target is valid
                if ((!targetId || targetId === "end") && edge.target && !edge.target.includes("coachmark_")) {
                    // Use edge.target directly - it should be the node ID
                    targetId = edge.target;
                }
            }
        }
        
        // For switchCase, each branch gets ONE condition from the switchConditions array
        // Case 1 gets switchConditions[0], Case 2 gets switchConditions[1], etc.
        let branchCondition;
        if (switchConditions.length > i) {
            // Use the condition at index i for this branch
            // The condition is already a complete condition object (e.g., { eq: { var: "...", value: true } })
            branchCondition = switchConditions[i];
        } else if (switchConditions.length > 0) {
            // If we have fewer conditions than branches, use the last condition for remaining branches
            branchCondition = switchConditions[switchConditions.length - 1];
        } else {
            // No conditions at all - use empty condition
            branchCondition = { and: [] };
        }
        
        // Create separate branches for each condition, even without targets
        // This ensures conditions are separate from the start, not combined in an 'and' array
        const branch = {
            condition: branchCondition,
            steps: [], // Always include steps array (empty if no actions connected)
        };
        
        // Only add 'next' property if there's a valid target (not "end" or undefined)
        // Branches without targets will have empty steps array, but the condition structure is preserved
        if (targetId && targetId !== "end") {
            branch.next = targetId;
        }
        
        switchBranches.push(branch);
    }
    
    // Get default edge (Default branch)
    const defaultEdge = connectedEdges.find(edge => 
        edge.data.branchLabel === "Default"
    );
    const defaultTarget = defaultEdge ? getNextTarget([defaultEdge], events) : "end";
    
    const returnedObject = {
        main: [
            {
                steps: [
                    {
                        name: `${FEEvent.id}`,
                        action: "SubAutomation.Call",
                        args: {
                            sub_automation: FEEvent.id,
                        },
                        next: "end",
                        shadows: shadows,
                        inTos: inTos,
                    },
                ],
            },
        ],
        sub: [
            {
                name: FEEvent.id,
                params: [],
                type: "switchCase",
                noOfLinks: FEEvent.data.configs?.noOfLinks || expectedCaseCount, // Store noOfLinks for persistence
                steps: [
                    ...mainSteps,
                    {
                        action: "Condition.Switch",
                        name: FEEvent.id,
                        args: {
                            // Always use switchBranches - they're now created for all conditions
                            // even without targets, ensuring separate conditions from the start
                            switch: switchBranches.length > 0 ? switchBranches : [
                                {
                                    condition: { and: [] },
                                }
                            ],
                        },
                        next: defaultTarget !== "end" ? defaultTarget : undefined,
                    },
                ],
            },
        ],
        reusableSubAutomations: subAutomationsNames,
    };
    return returnedObject;
};

export { parseBESwitchCaseToFE, parseFESwitchCaseToBE };